import axios from 'axios';
import Cookies from 'js-cookie';
import { BASE_URL } from '@/helpers/apiUtilities.js';
import helpers from '@/helpers/helpers.js';
import alerts from '../helpers/storeModuleHelpers/alerts';
import _cloneDeep from 'lodash/cloneDeep';
import VueAnalytics from '@/plugins/vueAnalytics.js';
import { parseISO } from 'date-fns';

export const state = {
  currentUser: {},
  localUserCopy: {},
  userToken: null,
  savingUser: false,
  savingPassword: false,
  loading: false,
  deactivating: false,
  ...alerts.getState([
    'login',
    'accountSettings',
    'profile',
    'deactivation'
  ])
};

export const mutations = {
  ...alerts.mutations,
  setUserObject: function (state, payload) {
    state.currentUser = payload;
    state.localUserCopy = _cloneDeep(payload);
  },
  setUserToken: function (state, token) {
    state.userToken = token;

    if (token) {
      const in30Minutes = 1 / 48;
      Cookies.set('user-token', token, {
        expires: in30Minutes
      });
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      Cookies.remove('user-token');
      delete axios.defaults.headers.common.Authorization;
    }
  },
  setSavingUser: function (state, bool) {
    state.savingUser = bool;
  },
  setSavingPassword: function (state, bool) {
    state.savingPassword = bool;
  },
  setDeactivating: function (state, bool) {
    state.deactivating = bool;
  },
  setLoading: function (state, bool) {
    state.loading = bool;
  },
  resetServerData: function (state, bool) {
    state.localUserCopy = _cloneDeep(state.currentUser);
  }
};

export const actions = {
  validateUser: function (store, token) {
    store.commit('clearAlerts', 'login');
    store.commit('setUserToken', token);
    return axios.post(BASE_URL + '/ValidateUserToken')
      .then(response => {
        const token = response?.data?.latestToken;
        store.commit('setUserToken', token);
        store.dispatch('loadUser');
        return true;
      })
      .catch((error) => {
        store.commit('setUserToken', null);
        if (error?.response?.status === 401) {
          const alert = {
            alertGroup: 'login',
            status: 'error',
            message: 'Not valid authorization.'
          };
          store.commit('addAlert', alert);
        } else {
          const alert = {
            alertGroup: 'login',
            status: 'error',
            message: 'Error logging in:',
            error
          };
          store.commit('addAlert', alert);
        }
        return false;
      });
  },
  login: function (store, auth) {
    store.commit('clearAlerts', 'login');
    return axios.post(BASE_URL + '/AuthenticateUser', {}, { auth })
      .then(response => {
        const token = response?.data?.token;
        store.commit('setUserToken', token);
        store.dispatch('loadUser');
        return true;
      })
      .catch((error) => {
        store.commit('setUserToken', null);
        if (error?.response?.status === 401) {
          const alert = {
            alertGroup: 'login',
            status: 'error',
            message: 'Incorrect email or password.'
          };
          store.commit('addAlert', alert);
        } else {
          const alert = {
            alertGroup: 'login',
            status: 'error',
            message: 'Error logging in:',
            error
          };
          store.commit('addAlert', alert);
        }
        return false;
      });
  },
  resetPassword: function (store, username) {
    store.commit('clearAlerts', 'login');
    return axios.post(BASE_URL + '/ClientForgotPassword/' + username)
      .then(response => {
        const alert = {
          alertGroup: 'login',
          status: 'success',
          message: 'You have successfully sent a reset password request. Please wait for the email.'
        };
        store.commit('addAlert', alert);
        return true;
      })
      .catch((error) => {
        const alert = {
          alertGroup: 'login',
          status: 'error',
          message: 'Error reseting password:',
          error
        };
        store.commit('addAlert', alert);
        return false;
      });
  },
  logout: function (store) {
    return new Promise((resolve, reject) => {
      store.commit('setUserObject', {});
      store.commit('setUserToken', null);
      store.dispatch('resetData');
      resolve();
    });
  },
  initializeAuth: function (store) {
    const token = Cookies.get('user-token');
    if (token) {
      store.commit('setUserToken', token);
      store.dispatch('loadUser');
    }
  },
  getUser: function (store) {
    store.commit('setLoading', true);

    return axios.get(BASE_URL + '/User')
      .then(response => {
        store.commit('setUserObject', response.data);
        store.dispatch('cart/setupCart');
        return response.data;
      })
      .catch((error) => {
        const alert = {
          alertGroup: 'profile',
          status: 'error',
          message: 'Error getting user profile:',
          error
        };
        store.commit('addAlert', alert);
      })
      .finally((response) => {
        store.commit('setLoading', false);
        return response;
      });
  },
  loadUser: async function (store) {
    store.commit('setLoading', true);

    const user = await store.dispatch('getUser');
    if (user) {
      VueAnalytics.setup();
      store.dispatch('signalR/createConnection', user.id, { root: true });
    }
  },
  updateUser: async function (store, user) {
    store.commit('setLoading', true);

    return axios.patch(BASE_URL + '/User', user)
      .then(response => {
        store.commit('setUserObject', response.data);
        return response.data;
      })
      .catch((error) => {
        const alert = {
          alertGroup: 'profile',
          status: 'error',
          message: 'Error updating user profile:',
          error
        };
        store.commit('addAlert', alert);
      })
      .finally((response) => {
        store.commit('setLoading', false);
        return response;
      });
  },
  setupSignalR: function (store, connection) {
    return connection.on('userUpdated', (response) => {
      store.dispatch('getUser');
    });
  }
};

export const getters = {
  isDeactive: function (state) {
    if (state.currentUser?.disabledDate !== '0001-01-01T00:00:00') {
      const date = parseISO(state.currentUser.disabledDate);
      return date <= new Date();
    }
    return false;
  },
  isAuthenticated: function (state) {
    return !!state.userToken;
  },
  currentUserFullName: function (state) {
    return helpers.getFullName(state.currentUser);
  },
  currentUserInitials: function (state) {
    if (state.currentUser && (state.currentUser.firstName || state.currentUser.lastName)) {
      return helpers.getInitials(state.currentUser);
    }

    return '';
  },
  currentUserAvatarUrl: function (state) {
    if (state.currentUser?.profileImageName) {
      return 'https://preventiab2e9.blob.core.windows.net/user-profile/' + state.currentUser.profileImageName;
    }
  }
};

export default {
  namespaced: true,
  state,
  mutations
};
